/**
 * vinculacionService.js
 * 
 * FASE 1: Vinculación automática de líneas de factura con productos del catálogo.
 * 
 * Algoritmo de matching (por orden de prioridad):
 *   1. EAN exacto                    → confianza 100, método EAN
 *   2. Referencia proveedor exacta   → confianza 100, método REFERENCIA_PROVEEDOR
 *   3. Código interno exacto         → confianza 100, método CODIGO_INTERNO
 *   4. Código Epelsa exacto          → confianza 100, método CODIGO_EPELSA
 *   5. Nombre normalizado muy similar → confianza 90, método NOMBRE
 *   6. Sin coincidencia segura        → PENDIENTE_VINCULACION
 * 
 * NUNCA crea productos automáticamente.
 * NUNCA vincula por nombre si la coincidencia es dudosa (< 85%).
 */

import { tiendaDb } from '../lib/supabase';

// ─── NORMALIZACIÓN DE TEXTO ───────────────────────────────────────────────────
const normalizar = (texto) => {
  if (!texto) return '';
  return (
    // solo alfanumérico
    // quitar acentos
    texto?.toLowerCase()?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-z0-9\s]/g, ' ')?.replace(/\s+/g, ' ')?.trim()
  );
};

// Similitud de Jaccard sobre bigramas
const similitudTexto = (a, b) => {
  const na = normalizar(a);
  const nb = normalizar(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;

  const bigramas = (s) => {
    const set = new Set();
    for (let i = 0; i < s?.length - 1; i++) set?.add(s?.slice(i, i + 2));
    return set;
  };

  const ba = bigramas(na);
  const bb = bigramas(nb);
  if (ba?.size === 0 || bb?.size === 0) return 0;

  let interseccion = 0;
  ba?.forEach(b => { if (bb?.has(b)) interseccion++; });
  const union = ba?.size + bb?.size - interseccion;
  return union > 0 ? interseccion / union : 0;
};

// ─── BUSCAR PRODUCTO EN CATÁLOGO ─────────────────────────────────────────────
/**
 * Intenta localizar un producto existente para una línea de factura.
 * Devuelve { producto, metodo, confianza } o null si no hay coincidencia segura.
 */
export const buscarProductoParaLinea = async (linea, productos) => {
  const { ean_proveedor, referencia_proveedor, descripcion, descripcion_original } = linea;
  const descTexto = descripcion_original || descripcion || '';

  // 1. EAN exacto
  if (ean_proveedor?.trim()) {
    const ean = ean_proveedor?.trim();
    const match = productos?.find(p => p?.ean?.trim() === ean);
    if (match) return { producto: match, metodo: 'EAN', confianza: 100 };
  }

  // 2. Referencia proveedor exacta (en ocr_product_mappings primero)
  if (referencia_proveedor?.trim()) {
    const ref = referencia_proveedor?.trim()?.toLowerCase();
    const match = productos?.find(p =>
      p?.referencia_proveedor?.trim()?.toLowerCase() === ref
    );
    if (match) return { producto: match, metodo: 'REFERENCIA_PROVEEDOR', confianza: 100 };
  }

  // 3. Código interno exacto
  if (referencia_proveedor?.trim()) {
    const ref = referencia_proveedor?.trim()?.toLowerCase();
    const match = productos?.find(p =>
      p?.codigo_interno?.trim()?.toLowerCase() === ref
    );
    if (match) return { producto: match, metodo: 'CODIGO_INTERNO', confianza: 100 };
  }

  // 4. Código Epelsa exacto
  if (referencia_proveedor?.trim()) {
    const ref = referencia_proveedor?.trim()?.toLowerCase();
    const match = productos?.find(p =>
      p?.codigo_epelsa?.trim()?.toLowerCase() === ref
    );
    if (match) return { producto: match, metodo: 'CODIGO_EPELSA', confianza: 100 };
  }

  // 5. Nombre normalizado (solo si similitud >= 0.85)
  if (descTexto?.trim()) {
    let mejorMatch = null;
    let mejorSimilitud = 0;

    for (const p of (productos || [])) {
      const sim = similitudTexto(descTexto, p?.nombre);
      if (sim > mejorSimilitud) {
        mejorSimilitud = sim;
        mejorMatch = p;
      }
    }

    if (mejorSimilitud >= 0.85) {
      return {
        producto: mejorMatch,
        metodo: 'NOMBRE',
        confianza: Math.round(mejorSimilitud * 100),
      };
    }
  }

  return null;
};

// ─── OBTENER EQUIVALENCIAS APRENDIDAS ────────────────────────────────────────
/**
 * Busca en ocr_product_mappings si ya existe una equivalencia aprendida
 * para este proveedor + descripción/referencia/EAN.
 */
export const buscarEquivalenciaAprendida = async (linea, proveedorId) => {
  if (!proveedorId) return null;

  const { ean_proveedor, referencia_proveedor, descripcion, descripcion_original } = linea;
  const descTexto = descripcion_original || descripcion || '';

  // Buscar por EAN
  if (ean_proveedor?.trim()) {
    const { data } = await tiendaDb
      ?.from('ocr_product_mappings')
      ?.select('*, producto:productos(id, nombre, precio_compra, pvp_venta, ean, codigo_epelsa)')
      ?.eq('proveedor_id', proveedorId)
      ?.eq('ean_proveedor', ean_proveedor?.trim())
      ?.eq('activo', true)
      ?.limit(1);
    if (data?.[0]?.producto) {
      return { mapping: data?.[0], metodo: 'EAN', confianza: 100 };
    }
  }

  // Buscar por referencia proveedor
  if (referencia_proveedor?.trim()) {
    const { data } = await tiendaDb
      ?.from('ocr_product_mappings')
      ?.select('*, producto:productos(id, nombre, precio_compra, pvp_venta, ean, codigo_epelsa)')
      ?.eq('proveedor_id', proveedorId)
      ?.eq('referencia_proveedor', referencia_proveedor?.trim())
      ?.eq('activo', true)
      ?.limit(1);
    if (data?.[0]?.producto) {
      return { mapping: data?.[0], metodo: 'REFERENCIA_PROVEEDOR', confianza: 100 };
    }
  }

  // Buscar por descripción normalizada
  if (descTexto?.trim()) {
    const { data } = await tiendaDb
      ?.from('ocr_product_mappings')
      ?.select('*, producto:productos(id, nombre, precio_compra, pvp_venta, ean, codigo_epelsa)')
      ?.eq('proveedor_id', proveedorId)
      ?.eq('activo', true);

    if (data?.length) {
      let mejorMapping = null;
      let mejorSim = 0;
      for (const m of data) {
        const sim = similitudTexto(descTexto, m?.descripcion_proveedor);
        if (sim > mejorSim) { mejorSim = sim; mejorMapping = m; }
      }
      if (mejorSim >= 0.85 && mejorMapping?.producto) {
        return { mapping: mejorMapping, metodo: 'NOMBRE', confianza: Math.round(mejorSim * 100) };
      }
    }
  }

  return null;
};

// ─── VINCULAR LÍNEA A PRODUCTO ────────────────────────────────────────────────
/**
 * Vincula una línea de factura a un producto existente.
 * Actualiza coste del producto y guarda histórico.
 * Aprende la equivalencia para futuras facturas.
 */
export const vincularLineaAProducto = async ({
  lineaId,
  productoId,
  facturaId,
  proveedorId,
  metodo = 'MANUAL',
  confianza = 100,
  linea,
}) => {
  // 1. Actualizar la línea
  const { error: errLinea } = await tiendaDb
    ?.from('lineas_factura_compra')
    ?.update({
      producto_id: productoId,
      estado_vinculacion: 'VINCULADA',
      metodo_vinculacion: metodo,
      confianza_vinculacion: confianza,
    })
    ?.eq('id', lineaId);
  if (errLinea) throw errLinea;

  // 2. Obtener datos actuales del producto
  const { data: producto, error: errProd } = await tiendaDb
    ?.from('productos')
    ?.select('id, nombre, precio_compra, pvp_venta, pvp_sugerido, margen_objetivo, tipo_iva, recargo_equivalencia, proveedor_principal_id, bloqueo_precio, coste_anterior')
    ?.eq('id', productoId)
    ?.single();
  if (errProd) throw errProd;

  // 3. Calcular nuevo coste
  const precioNeto = linea?.precio_compra_neto || linea?.precio_unitario || 0;
  const iva = linea?.porcentaje_iva ?? producto?.tipo_iva ?? 10;
  const recargo = producto?.recargo_equivalencia ?? 1.4;
  const descuento = linea?.descuento_porcentaje ?? 0;
  const precioConDescuento = precioNeto * (1 - descuento / 100);
  const precioConIva = precioConDescuento * (1 + iva / 100);
  const costeConImpuestos = precioConDescuento * (1 + iva / 100 + recargo / 100);

  const costoAnterior = producto?.precio_compra || null;

  // 4. Guardar histórico de coste
  await tiendaDb?.from('historico_precios_compra')?.insert({
    producto_id: productoId,
    proveedor_id: proveedorId || null,
    factura_compra_id: facturaId || null,
    linea_factura_compra_id: lineaId,
    fecha: new Date()?.toISOString()?.split('T')?.[0],
    precio_bruto: linea?.precio_unitario || precioNeto,
    descuento_porcentaje: descuento,
    precio_neto: precioConDescuento,
    iva_porcentaje: iva,
    recargo_equivalencia_pct: recargo,
    coste_con_impuestos: costeConImpuestos,
    pvp_actual: producto?.pvp_venta || null,
    pvp_sugerido: producto?.pvp_sugerido || null,
    margen_aplicado: producto?.margen_objetivo || null,
  });

  // 5. (precio_revisar pertenece a la siguiente fase de Precios — omitido en FASE 1)

  // 6. Actualizar producto con nuevo coste
  const updatePayload = {
    precio_compra: precioConDescuento,
    precio_compra_con_iva: precioConIva,
    coste_con_impuestos: costeConImpuestos,
    coste_anterior: costoAnterior,
    updated_at: new Date()?.toISOString(),
  };

  // Asignar proveedor principal solo si no tiene uno
  if (!producto?.proveedor_principal_id && proveedorId) {
    updatePayload.proveedor_principal_id = proveedorId;
  }

  const { error: errUpdate } = await tiendaDb
    ?.from('productos')
    ?.update(updatePayload)
    ?.eq('id', productoId);
  if (errUpdate) throw errUpdate;

  // 7. Aprender equivalencia en ocr_product_mappings
  if (proveedorId) {
    await aprenderEquivalencia({
      productoId,
      proveedorId,
      descripcionProveedor: linea?.descripcion_original || linea?.descripcion,
      referenciaProveedor: linea?.referencia_proveedor,
      eanProveedor: linea?.ean_proveedor,
      metodo,
    });
  }

  return { ok: true, costoAnterior, costoNuevo: precioConDescuento };
};

// ─── APRENDER EQUIVALENCIA ────────────────────────────────────────────────────
export const aprenderEquivalencia = async ({
  productoId,
  proveedorId,
  descripcionProveedor,
  referenciaProveedor,
  eanProveedor,
  metodo = 'MANUAL',
}) => {
  if (!productoId || !proveedorId) return;

  // Buscar si ya existe esta equivalencia
  let query = tiendaDb
    ?.from('ocr_product_mappings')
    ?.select('id, veces_confirmado')
    ?.eq('proveedor_id', proveedorId)
    ?.eq('producto_id', productoId);

  if (eanProveedor?.trim()) {
    query = query?.eq('ean_proveedor', eanProveedor?.trim());
  } else if (referenciaProveedor?.trim()) {
    query = query?.eq('referencia_proveedor', referenciaProveedor?.trim());
  } else if (descripcionProveedor?.trim()) {
    query = query?.eq('descripcion_proveedor', descripcionProveedor?.trim());
  } else {
    return; // Sin datos suficientes para aprender
  }

  const { data: existing } = await query?.limit(1);

  if (existing?.[0]) {
    // Actualizar contador de confirmaciones
    await tiendaDb
      ?.from('ocr_product_mappings')
      ?.update({
        veces_confirmado: (existing?.[0]?.veces_confirmado || 1) + 1,
        ultima_confirmacion: new Date()?.toISOString(),
        metodo_vinculacion: metodo,
        activo: true,
      })
      ?.eq('id', existing?.[0]?.id);
  } else {
    // Crear nueva equivalencia
    await tiendaDb?.from('ocr_product_mappings')?.insert({
      proveedor_id: proveedorId,
      producto_id: productoId,
      descripcion_proveedor: descripcionProveedor?.trim() || '',
      referencia_proveedor: referenciaProveedor?.trim() || null,
      ean_proveedor: eanProveedor?.trim() || null,
      confianza: 100,
      veces_confirmado: 1,
      metodo_vinculacion: metodo,
      activo: true,
    });
  }
};

// ─── IGNORAR LÍNEA ────────────────────────────────────────────────────────────
export const ignorarLinea = async (lineaId) => {
  const { error } = await tiendaDb
    ?.from('lineas_factura_compra')
    ?.update({ estado_vinculacion: 'IGNORADA' })
    ?.eq('id', lineaId);
  if (error) throw error;
};

// ─── OBTENER LÍNEAS PENDIENTES ────────────────────────────────────────────────
export const getLineasPendientes = async () => {
  const { data, error } = await tiendaDb?.rpc('get_lineas_pendientes_vinculacion');
  if (error) {
    // Fallback directo
    const { data: fallback, error: err2 } = await tiendaDb
      ?.from('lineas_factura_compra')
      ?.select(`
        *,
        factura:facturas_compra(id, numero_factura, proveedor_id, proveedor:proveedores(id, nombre))
      `)
      ?.is('producto_id', null)
      ?.neq('estado_vinculacion', 'IGNORADA')
      ?.order('created_at', { ascending: false });
    if (err2) throw err2;
    return (fallback || [])?.map(l => ({
      ...l,
      numero_factura: l?.factura?.numero_factura,
      proveedor_id: l?.factura?.proveedor_id,
      proveedor_nombre: l?.factura?.proveedor?.nombre,
    }));
  }
  return data || [];
};

// ─── OBTENER INDICADORES ──────────────────────────────────────────────────────
export const getIndicadoresVinculacion = async () => {
  const { data, error } = await tiendaDb?.rpc('get_indicadores_vinculacion');
  if (error) {
    // Fallback manual
    const [prods, lineas] = await Promise.all([
      tiendaDb?.from('productos')?.select('id, precio_compra, proveedor_principal_id')?.eq('activo', true),
      tiendaDb?.from('lineas_factura_compra')?.select('id, producto_id, estado_vinculacion'),
    ]);
    const ps = prods?.data || [];
    const ls = lineas?.data || [];
    return {
      total_productos: ps?.length,
      productos_con_coste: ps?.filter(p => p?.precio_compra > 0)?.length,
      productos_sin_coste: ps?.filter(p => !p?.precio_compra || p?.precio_compra <= 0)?.length,
      productos_con_proveedor: ps?.filter(p => p?.proveedor_principal_id)?.length,
      productos_sin_proveedor: ps?.filter(p => !p?.proveedor_principal_id)?.length,
      lineas_totales: ls?.length,
      lineas_vinculadas: ls?.filter(l => l?.producto_id)?.length,
      lineas_pendientes: ls?.filter(l => !l?.producto_id && l?.estado_vinculacion !== 'IGNORADA')?.length,
      lineas_ignoradas: ls?.filter(l => l?.estado_vinculacion === 'IGNORADA')?.length,
    };
  }
  return data?.[0] || {};
};

// ─── PROCESAR VINCULACIONES PENDIENTES (BATCH) ────────────────────────────────
/**
 * Revisa todas las líneas pendientes e intenta vincularlas automáticamente.
 * Solo vincula si confianza >= 90 (EAN, ref exacta, o nombre muy similar).
 * Devuelve estadísticas del proceso.
 */
export const procesarVinculacionesPendientes = async () => {
  const stats = { procesadas: 0, vinculadas: 0, pendientes: 0, errores: 0 };

  // Cargar líneas pendientes
  const lineas = await getLineasPendientes();
  if (!lineas?.length) return stats;

  // Cargar catálogo completo
  const { data: productos } = await tiendaDb
    ?.from('productos')
    ?.select('id, nombre, ean, referencia_proveedor, codigo_interno, codigo_epelsa, precio_compra, pvp_venta, tipo_iva, recargo_equivalencia, proveedor_principal_id, margen_objetivo, bloqueo_precio')
    ?.eq('activo', true);

  for (const linea of lineas) {
    stats.procesadas++;
    try {
      // Primero buscar en equivalencias aprendidas
      let resultado = null;
      if (linea?.proveedor_id) {
        const equiv = await buscarEquivalenciaAprendida(linea, linea?.proveedor_id);
        if (equiv) {
          resultado = {
            producto: equiv?.mapping?.producto,
            metodo: equiv?.metodo,
            confianza: equiv?.confianza,
          };
        }
      }

      // Si no hay equivalencia, buscar en catálogo
      if (!resultado) {
        resultado = await buscarProductoParaLinea(linea, productos);
      }

      if (resultado && resultado?.confianza >= 90) {
        await vincularLineaAProducto({
          lineaId: linea?.id,
          productoId: resultado?.producto?.id,
          facturaId: linea?.factura_id,
          proveedorId: linea?.proveedor_id,
          metodo: resultado?.metodo,
          confianza: resultado?.confianza,
          linea,
        });
        stats.vinculadas++;
      } else {
        stats.pendientes++;
      }
    } catch (e) {
      console.error('Error vinculando línea', linea?.id, e);
      stats.errores++;
    }
  }

  return stats;
};

// ─── BUSCAR PRODUCTOS PARA VINCULAR MANUALMENTE ───────────────────────────────
export const buscarProductosParaVincular = async (query) => {
  if (!query?.trim()) return [];
  const q = query?.trim()?.toLowerCase();
  const { data, error } = await tiendaDb
    ?.from('productos')
    ?.select('id, nombre, ean, plu, codigo_interno, codigo_epelsa, referencia_proveedor, precio_compra, pvp_venta, proveedor_principal_id, proveedor:proveedores(nombre)')
    ?.or(`nombre.ilike.%${q}%,ean.ilike.%${q}%,plu.ilike.%${q}%,codigo_interno.ilike.%${q}%,codigo_epelsa.ilike.%${q}%`)
    ?.eq('activo', true)
    ?.limit(20);
  if (error) throw error;
  return data || [];
};

export default {
  buscarProductoParaLinea,
  buscarEquivalenciaAprendida,
  vincularLineaAProducto,
  aprenderEquivalencia,
  ignorarLinea,
  getLineasPendientes,
  getIndicadoresVinculacion,
  procesarVinculacionesPendientes,
  buscarProductosParaVincular,
};
